import { useDisclosure } from '@chakra-ui/react';
import { Box, Button, Icon, Link, Stack } from '@hover/blueprint';
import { iMenu, iX } from '@hover/icons';
import { Link as GatsbyLink } from 'gatsby';
import React, { FC, useState } from 'react';
import { useLockedBody } from '../../../hooks';
import { HoverLogo, HoverLogoWhite } from '../../../icons';
import { INavbarItem } from '../../../interfaces/INavbarItems';

import NavbarDownloadAppButton from './NavbarDownloadAppButton';
import NavbarMobileItem from './NavbarMobileItem';

export interface NavbarMobileProps {
  links: INavbarItem[];
  isNavbarWhite: boolean;
  navbarScrolled: boolean;
  navbarColor: string | undefined;
  showDivider: boolean;
  dividerColor?: string;
  buttonType: string;
}

const NavbarMobile: FC<NavbarMobileProps> = ({
  links,
  isNavbarWhite,
  navbarScrolled,
  navbarColor,
  showDivider,
  dividerColor,
  buttonType,
}) => {
  const [activeTabLabel, setActiveTabLabel] = useState<string | null>(null);

  const { isOpen, onToggle } = useDisclosure();
  const { setLocked } = useLockedBody();

  function handleActiveTab(label: string) {
    if (activeTabLabel === label) setActiveTabLabel(null);
    else setActiveTabLabel(label);
  }

  const toggleNavbar = () => {
    setLocked(!isOpen);
    onToggle();
  };

  const mobileNavColor = isOpen ? 'neutral.0' : navbarColor;
  const isWhiteBackground = isOpen || isNavbarWhite;

  const getBorderColor = () => {
    if (!showDivider || navbarScrolled) return 'transparent';
    if (isWhiteBackground) return dividerColor;

    return 'neutral.0';
  };

  return (
    <Box
      px={500}
      transition="background 0.3s ease"
      display="flex"
      flexDirection="column"
      width="100%"
      bg={mobileNavColor}
      borderColor={getBorderColor()}
      borderWidth={0}
      borderStyle="solid"
      borderBottomWidth={400}
    >
      <Box
        display="flex"
        py="5"
        alignItems="center"
        justifyContent="space-between"
        height="64px"
      >
        <Link as={GatsbyLink} to={`${process.env.GATSBY_V2_PREFIX}/`}>
          {isWhiteBackground ? (
            <HoverLogo
              title="HOVER"
              alt="HOVER Logo"
              style={{ width: 98, height: 24 }}
            />
          ) : (
            <HoverLogoWhite
              title="HOVER"
              alt="HOVER Logo"
              style={{ width: 98, height: 24 }}
            />
          )}
        </Link>
        <Button
          color="neutral"
          fill="minimal"
          label="Menu"
          shape="circle"
          size="large"
          onClick={toggleNavbar}
        >
          <Icon
            size="default"
            color={!isWhiteBackground ? 'neutral.0' : 'primary.600'}
            icon={isOpen ? iX : iMenu}
          />
        </Button>
      </Box>
      {isOpen ? (
        <Box
          position="fixed"
          left={0}
          right={0}
          top={900}
          bottom={0}
          zIndex="10"
          overflowY="scroll"
          bg="neutral.0"
          display="flex"
          flexDir="column"
        >
          <Stack
            px={500}
            py={700}
            display={{ desktop: 'none', base: 'flex' }}
            w="100%"
          >
            {links.map(navItem => (
              <NavbarMobileItem
                {...navItem}
                key={navItem.label}
                isOpen={activeTabLabel === navItem.label}
                onToggle={() => {
                  if (navItem.children) handleActiveTab(navItem.label);
                }}
              />
            ))}
          </Stack>
          <Box display="flex" alignItems="center" justifyContent="center">
            <NavbarDownloadAppButton buttonType={buttonType} />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default NavbarMobile;
